import { Smartphone, MailPlus, Target, LineChart, LucideIcon } from "lucide-react";

interface Feature {
  icon: LucideIcon;
  tag: string;
  title: string;
  text: string;
  elite?: boolean;
}

const FEATURES: Feature[] = [
  {
    icon: Smartphone,
    tag: "Social",
    title: "Done-for-You Social Media",
    text: "Consistent, on-brand content published for you every week — so your audience sees you while you focus on relationships.",
  },
  {
    icon: MailPlus,
    tag: "Leads",
    title: "Done-for-You Lead Generation",
    text: "Qualified prospects delivered monthly through outreach campaigns tuned for recurring income offers.",
  },
  {
    icon: Target,
    tag: "ELITE only",
    title: "Done-for-You Discovery Calls",
    text: "A certified Growth Advisor professionally introduces the WinCampaign opportunity to qualified prospects on your behalf.",
    elite: true,
  },
  {
    icon: LineChart,
    tag: "Strategy",
    title: "Monthly Growth Review",
    text: "A 1:1 review tuning your content, offers, and lead flow based on what's working in your market.",
  },
];

export default function WhatsIncluded() {
  return (
    <section
      id="included"
      data-testid="wc-included"
      className="bg-white py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-[52px] tracking-tight text-[#30265b] leading-[1.05]">
            Your dedicated marketing team{" "}
            <span className="text-[#673dfc]">
              — working behind your business every month.
            </span>
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
            Four pillars of consistent growth, quietly running in the
            background while you focus on relationships and closing new
            subscribers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-14 sm:mt-16">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className={`group relative rounded-3xl border p-8 sm:p-10 transition-all duration-500 ${
                f.elite
                  ? "bg-[#30265b] text-white border-[#673dfc]/40 shadow-xl shadow-[#673dfc]/10 hover:shadow-2xl hover:shadow-[#673dfc]/25 hover:-translate-y-1"
                  : "bg-white border-gray-100 hover:border-[#c5b4ff] hover:shadow-lg hover:shadow-[#673dfc]/5 hover:-translate-y-1"
              }`}
            >
              <div className="flex items-start justify-between mb-8">
                <div
                  className={`w-14 h-14 rounded-2xl grid place-items-center transition-transform duration-500 group-hover:scale-105 ${
                    f.elite
                      ? "bg-[#d3fc3a] text-[#1a1a24]"
                      : "bg-[#f3efff] text-[#673dfc]"
                  }`}
                >
                  <f.icon size={24} strokeWidth={1.75} />
                </div>
                <span
                  className={`text-[10px] font-nav font-semibold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${
                    f.elite
                      ? "bg-[#d3fc3a] text-[#1a1a24]"
                      : "bg-[#f3efff] text-[#673dfc]"
                  }`}
                >
                  {f.tag}
                </span>
              </div>
              <div
                className={`font-display font-bold text-2xl sm:text-[26px] leading-[1.15] ${
                  f.elite ? "text-white" : "text-[#30265b]"
                }`}
              >
                {f.title}
              </div>
              <p
                className={`mt-4 text-base leading-relaxed ${
                  f.elite ? "text-white/75" : "text-gray-600"
                }`}
              >
                {f.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
